import { forwardRef } from "react";

import { Theme, useTheme } from "@mui/material";

import { InvalidThemeErrorMessage } from "../InvalidThemeErrorMessage";

import { DashboardTypography, TypographyProps } from "@/dashboard-design-system/Typography";
import { mergeSx } from "@/helpers/mergeSx";

export type FormTypographyProps = Omit<TypographyProps, "vibe"> & {
  color?: string;
  vibe?: "intense" | "moderate" | "brand" | "supershy" | "reverse" | "error";
};

export const FormTypography = forwardRef<HTMLParagraphElement, FormTypographyProps>(
  ({ vibe, sx, children, color, ...rest }, ref) => {
    const theme = useTheme();

    if (!theme.palette.forms) {
      return <InvalidThemeErrorMessage />;
    }

    const getColor = (theme: Theme) => {
      switch (vibe) {
        case "intense":
          return theme.palette.forms.text.intense;
        case "moderate":
          return theme.palette.forms.text.moderate;
        case "brand":
          return theme.palette.forms.text.brand;
        case "supershy":
          return theme.palette.forms.text.supershy;
        case "reverse":
          return theme.palette.forms.text.reverse;
        case "error":
          return theme.palette.forms.text.error;
        default:
          return "inherit";
      }
    };

    const styles = mergeSx(
      {
        color: color ?? getColor(theme),
      },
      sx
    );

    return (
      <DashboardTypography sx={styles} {...rest} ref={ref}>
        {children}
      </DashboardTypography>
    );
  }
);

FormTypography.displayName = "FormTypography";
